/**
 * 弹窗：成功 / 失败 / 设置 / 分享。
 *
 * 吉祥物是品牌表达的核心，所以每个弹窗都带一只在播帧动画的星星：
 * 成功用眨眼，失败用哭泣。
 */

import { t } from './i18n.js';

/** @type {HTMLElement | null} */
let host = null;

/** @param {HTMLElement} el 弹窗挂载点 */
export function initPopups(el) {
    host = el;
}

/** 关掉当前弹窗。 */
export function closePopup() {
    if (host) host.replaceChildren();
    document.body.classList.remove('sd-modal-open');
}

/**
 * 通用弹窗骨架。
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.text]
 * @param {string} [opts.mascot] mascot 修饰类，如 'blink' / 'cry'
 * @param {Array<{ label: string, kind?: 'primary'|'secondary', onClick: (btn: HTMLButtonElement) => void }>} opts.buttons
 * @param {boolean} [opts.dismissable] 点遮罩是否可关
 * @param {HTMLElement} [opts.extra] 额外内容，插在按钮之前
 * @param {string} [opts.variant] 弹窗变体，加 `sd-popup--<variant>` 修饰类
 * @param {HTMLElement} [opts.header] 标题之上的内容（如 logo），跟随内容区滚动
 * @param {() => void} [opts.onDismiss] 点遮罩关闭时的动作，默认直接关掉。
 *   从别的弹窗里叠出来时必须用它回到上一层 —— 弹窗共用一个宿主容器，
 *   showPopup 开头会清空它，直接 closePopup 等于把上一层也丢掉
 */
export function showPopup({
    title, text, mascot, buttons, dismissable = false, extra, variant, header, onDismiss,
}) {
    if (!host) return;
    closePopup();
    document.body.classList.add('sd-modal-open');

    const backdrop = document.createElement('div');
    backdrop.className = 'sd-backdrop';

    const box = document.createElement('div');
    box.className = 'sd-popup'
        + (mascot ? ' sd-popup--mascot' : '')
        + (variant ? ` sd-popup--${variant}` : '');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');

    if (mascot) {
        const m = document.createElement('div');
        m.className = `sd-mascot sd-mascot--${mascot} sd-mascot--playing sd-popup__mascot`;
        // 内层横条由 CSS 平移播帧，见 anim.css 里对 background-position 的说明
        const strip = document.createElement('i');
        strip.className = 'sd-mascot__strip';
        m.appendChild(strip);
        box.appendChild(m);
    }

    // 内容装在独立的一层里：矮视口下让它滚动，按钮留在外面永远够得着。
    // 弹窗默认点遮罩关不掉，内容顶出屏幕就等于把玩家关死在弹窗里
    const body = document.createElement('div');
    body.className = 'sd-popup__body';

    if (header) body.appendChild(header);

    const h = document.createElement('h2');
    h.className = 'sd-popup__title';
    h.textContent = title;
    body.appendChild(h);

    if (text) {
        const p = document.createElement('p');
        p.className = 'sd-popup__text';
        p.textContent = text;
        body.appendChild(p);
    }

    if (extra) body.appendChild(extra);
    box.appendChild(body);

    const row = document.createElement('div');
    row.className = 'sd-popup__buttons';
    for (const b of buttons) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `sd-btn sd-btn--${b.kind ?? 'primary'}`;
        btn.textContent = b.label;
        btn.addEventListener('click', () => b.onClick(btn));
        row.appendChild(btn);
    }
    box.appendChild(row);

    if (dismissable) {
        backdrop.addEventListener('click', e => {
            if (e.target === backdrop) (onDismiss ?? closePopup)();
        });
    }

    backdrop.appendChild(box);
    host.appendChild(backdrop);

    // 内容真的溢出时才加渐隐：矮屏上被截断的那一行需要一个「下面还有」的提示，
    // 否则玩家会直接点按钮走人，压根不知道漏了内容
    if (body.scrollHeight > body.clientHeight + 1) {
        body.classList.add('sd-popup__body--scrollable');
    }

    const first = box.querySelector('button');
    if (first) first.focus({ preventScroll: true });
}

// ── 新手引导 ──

/**
 * 造一个演示用的小格子。
 *
 * 复用棋盘的 `.sd-cellface` / `.sd-mark` 外观而不是另画一套图标：叉是 CSS 画的
 * （两根旋转圆角条，红叉只换 --sd-cross-color），另画必然对不上。这样引导里
 * 的标记与玩家下一秒在棋盘上看到的逐像素一致。
 *
 * 刻意不加 `.sd-cell` —— 那个类专指棋盘格，
 * 混进来会让 querySelectorAll('.sd-cell') 把这几个演示格也数进去。
 * @param {'empty'|'whiteCross'|'star'|'redCross'} state
 */
function demoCell(state) {
    const cell = document.createElement('div');
    cell.className = 'sd-cellface sd-demo-cell';
    cell.dataset.state = state;
    cell.appendChild(document.createElement('i')).className = 'sd-mark';
    return cell;
}

/**
 * 教学用的卡通小手。指尖在图形顶端（约 11.5, 3），CSS 靠这个点把手对到格子上，
 * 手身朝右下伸出去 —— 手压在格子右下角，才不会挡住格子里刚出现的叉或星，
 * 那是教学动画最容易踩的坑。
 *
 * 内联 SVG 而不是 PNG：描边随主题取色、缩放不糊，且不用为引导多加一份素材；
 * 以后美术出了正式的手，换掉这一段就行。这里是写死的静态标记，没有外部输入。
 */
function guideHand() {
    const touch = document.createElement('span');
    touch.className = 'sd-guide__touch';
    touch.setAttribute('aria-hidden', 'true');
    touch.innerHTML = '<svg class="sd-guide__hand" viewBox="0 0 30 39">'
        + '<path d="M9.25 6a2.75 2.75 0 0 1 5.5 0v9.5h4.75a4.75 4.75 0 0 1 4.75 4.75V26'
        + 'a8 8 0 0 1-8 8H11a8 8 0 0 1-8-8v-5.75a4.75 4.75 0 0 1 4.75-4.75h1.5z"/></svg>';
    return touch;
}

/**
 * 一列手势演示：上面是会自己动的小手 + 演示格，下面只有手势名。
 *
 * 三列共用 2.4s 的节拍（时间线见 anim.css），所以放在一起循环不会各跳各的。
 * 这里刻意不写任何解释文字 —— 「再点一次能取消」「叉可以连成一片」这些都由
 * 动画自己演出来，玩家看手就懂，读句子反而慢。
 * @param {'tap'|'drag'|'double'} kind 决定播哪条时间线
 * @param {string} op 手势名，唯一的文字
 * @param {Array<'empty'|'whiteCross'|'star'|'redCross'>} states 演示格的终态
 */
function guideCol(kind, op, states) {
    const col = document.createElement('div');
    col.className = `sd-guide__col sd-guide__col--${kind}`;

    const demo = document.createElement('div');
    demo.className = 'sd-guide__demo';

    const cells = document.createElement('div');
    cells.className = 'sd-guide__cells';
    for (const s of states) cells.appendChild(demoCell(s));

    demo.append(cells, guideHand());

    const label = document.createElement('p');
    label.className = 'sd-guide__op';
    label.textContent = op;

    col.append(demo, label);
    return col;
}

/** 底部一条小字：左边一个图标，右边一句话。 */
function footLine(icons, text) {
    const line = document.createElement('p');
    line.className = 'sd-guide__foot-line';
    line.append(...icons);
    const span = document.createElement('span');
    span.textContent = text;
    line.appendChild(span);
    return line;
}

/**
 * 欢迎与操作引导。每天第一关开局时放一次。
 * @param {object} o
 * @param {number} o.total 今天有几道题（读服务端实际题数，不写死）
 * @param {() => void} o.onStart
 */
export function showWelcome({ total, onStart }) {
    const logo = document.createElement('img');
    logo.className = 'sd-welcome__logo';
    logo.src = 'img/logo.webp';
    logo.alt = 'Stardoku';
    logo.width = 480;
    logo.height = 269;

    // 三个手势并排，一眼看完「一共就三个动作」。
    // 单击列的动画会把叉点出来、再点掉，取消不用另开一条说明
    const guide = document.createElement('div');
    guide.className = 'sd-guide';
    guide.append(
        guideCol('tap', t('play.welcome.opTap', 'Tap once'), ['whiteCross']),
        guideCol('drag', t('play.welcome.opDrag', 'Swipe across'),
            ['whiteCross', 'whiteCross', 'whiteCross']),
        guideCol('double', t('play.welcome.opDouble', 'Tap twice'), ['star']),
    );

    // 扣心和提示不进演示区：它们不是棋盘手势，混进三列会稀释「就三个动作」这件事。
    // 尤其红叉不能画在星那一列 —— 会被读成「星星会变成红叉」
    const hearts = document.createElement('span');
    hearts.className = 'sd-guide__hearts';
    hearts.append(heartIcon(true), heartIcon(true), heartIcon(false));

    const bulb = document.createElement('img');
    bulb.className = 'sd-guide__bulb';
    bulb.src = 'img/bulb.png';
    bulb.alt = '';

    const foot = document.createElement('div');
    foot.className = 'sd-guide__foot';
    foot.append(
        footLine([hearts], t('play.welcome.lives', 'A wrong star costs a heart — 3 in all')),
        footLine([bulb], t('play.welcome.hint', "Stuck? Tap the bulb, I'll point one out")),
    );

    const teach = document.createElement('div');
    teach.className = 'sd-welcome__teach';
    teach.append(guide, foot);

    showPopup({
        variant: 'welcome',
        header: logo,
        title: t('play.welcome.title', 'Welcome to Stardoku'),
        text: t('play.welcome.subtitle', '{n} puzzles a day, each harder than the last.')
            .replace('{n}', String(total)),
        // 这一屏刻意不放吉祥物：顶部只留放大的 logo，视线直接落到会动的三只手上
        extra: teach,
        buttons: [{ label: t('play.welcome.start', 'Start playing'), onClick: onStart }],
    });
}

/** @param {boolean} full */
function heartIcon(full) {
    const h = document.createElement('i');
    h.className = `sd-heart sd-heart--${full ? 'full' : 'empty'} sd-guide__heart`;
    return h;
}

/**
 * 通关。
 * @param {object} o
 * @param {number} o.level
 * @param {boolean} o.perfect 是否零失误
 * @param {boolean} o.hasNext
 * @param {() => void} o.onNext
 * @param {() => void} o.onHome
 */
export function showSuccess({ level, total, done, perfect, hasNext, onNext, onHome }) {
    // 这里不给分享入口：过关弹窗唯一的去处就是下一题。
    // 分享只留在「今天全部做完」的结束弹窗里，且分享的是网址而非题目。
    const buttons = hasNext
        ? [{ label: t('play.success.next', 'Next Level'), onClick: onNext }]
        : [{ label: t('play.daily.home', 'Back to site'), onClick: onHome }];

    let text = t('play.success.daily', '{done} of {total} done today.')
        .replace('{done}', String(done))
        .replace('{total}', String(total));
    if (perfect) {
        text = t('play.success.perfect', 'Flawless — not a single mistake!') + ' ' + text;
    }
    void level;

    showPopup({
        title: t('play.success.title', 'Level Complete!'),
        text,
        mascot: 'blink',
        buttons,
    });
}

/**
 * 失败。
 * @param {object} o
 * @param {() => void} o.onRetry
 * @param {() => void} o.onHome
 */
export function showFailure({ onRetry, onHome }) {
    showPopup({
        title: t('play.fail.title', 'Out of Lives'),
        text: t('play.fail.text', 'Take another run at it.'),
        mascot: 'cry',
        buttons: [
            { label: t('play.fail.retry', 'Try Again'), onClick: onRetry },
            { label: t('play.fail.home', 'Home'), kind: 'secondary', onClick: onHome },
        ],
    });
}

/**
 * 设置。
 * @param {object} o
 * @param {boolean} o.sound
 * @param {(on: boolean) => void} o.onSound
 */
export function showSettings({ sound, onSound }) {
    const wrap = document.createElement('div');
    wrap.className = 'sd-setting-row';

    const label = document.createElement('span');
    label.textContent = t('play.settings.sound', 'Sound');

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'sd-toggle';
    const sync = on => {
        toggle.classList.toggle('sd-toggle--on', on);
        toggle.setAttribute('aria-pressed', String(on));
        toggle.textContent = on ? t('play.settings.on', 'On') : t('play.settings.off', 'Off');
    };
    sync(sound);
    toggle.addEventListener('click', () => {
        sound = !sound;
        sync(sound);
        onSound(sound);
    });

    wrap.append(label, toggle);

    showPopup({
        title: t('play.settings.title', 'Settings'),
        mascot: 'shake',
        extra: wrap,
        dismissable: true,
        buttons: [{ label: t('play.close', 'Close'), onClick: closePopup }],
    });
}

/**
 * 分享：展示可复制的链接。
 *
 * 只接受游戏网址。题目的 sbn 串能直接解出题面与答案，绝不放进分享链接。
 *
 * @param {string} url
 * @param {() => void} [onClose] 关闭后回哪儿去。分享是从别的弹窗里叠出来的，
 *   不给返回路径就会把下面那层（尤其是还没填完的留资表单）一起丢掉，玩家没法再填。
 */
export function showShare(url, onClose) {
    const back = onClose ?? closePopup;
    const wrap = document.createElement('div');
    wrap.className = 'sd-share';

    const input = document.createElement('input');
    input.type = 'text';
    input.readOnly = true;
    input.value = url;
    input.className = 'sd-share__input';
    input.addEventListener('focus', () => input.select());

    const hint = document.createElement('p');
    hint.className = 'sd-popup__text';
    hint.textContent = t('play.share.hint', 'Send this link to a friend so they can play too.');

    wrap.append(hint, input);

    showPopup({
        title: t('play.share.title', 'Share Stardoku'),
        mascot: 'blink',
        extra: wrap,
        dismissable: true,
        onDismiss: back,
        buttons: [
            {
                label: t('play.share.copy', 'Copy link'),
                onClick: async () => {
                    try {
                        await navigator.clipboard.writeText(url);
                    } catch {
                        input.select();
                        document.execCommand?.('copy'); // 老 Safari 兜底
                    }
                    const btn = document.querySelector('.sd-popup__buttons .sd-btn--primary');
                    if (btn) btn.textContent = t('play.share.copied', 'Copied!');
                },
            },
            { label: t('play.close', 'Close'), kind: 'secondary', onClick: back },
        ],
    });
}

/**
 * 语言选单。选项一律用母语显示 —— 找自己的语言时，界面当前是什么语言不重要。
 * @param {object} o
 * @param {string} o.current
 * @param {Record<string, string>} o.names
 * @param {(lang: string) => void} o.onPick
 */
export function showLanguage({ current, names, onPick }) {
    const list = document.createElement('div');
    list.className = 'sd-langs';

    for (const [code, label] of Object.entries(names)) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sd-lang' + (code === current ? ' sd-lang--current' : '');
        btn.textContent = label;
        btn.lang = code;
        btn.addEventListener('click', () => onPick(code));
        list.appendChild(btn);
    }

    showPopup({
        title: t('play.settings.language', 'Language'),
        extra: list,
        dismissable: true,
        buttons: [{ label: t('play.close', 'Close'), kind: 'secondary', onClick: closePopup }],
    });
}

/**
 * 今天的题全做完了 —— 关不掉，明天才有新题。
 *
 * 没留过资：给昵称/邮箱表单，说明上线后有小礼物。
 * 留过资：只道贺，并带上他填的名字，不再重复索要。
 *
 * @param {object} o
 * @param {number} o.total
 * @param {import('./lead.js').Lead | null} o.lead 已保存的留资
 * @param {(input: {name: string, email: string}) => Promise<{ok: boolean, error?: string, lead?: any}>} o.onSubmit
 * @param {(draft: {name: string, email: string}) => void} o.onShare
 *   收到的是当前已输入但还没提交的内容 —— 分享回来要原样填回去，别让玩家白打一遍
 * @param {{name?: string, email?: string}} [o.draft] 回填用的草稿
 */
export function showDailyDone({ total, lead, onSubmit, onShare, draft }) {
    const tomorrow = t('play.daily.tomorrow',
        'Another {n} puzzles are waiting tomorrow.').replace('{n}', String(total));

    // ── 已经留过资：只道贺 ──
    if (lead) {
        // 选了「以后再说」时 lead 里没有名字，退回不带称呼的道贺
        const who = String(lead.name || lead.email || '').trim();
        const text = who
            ? t('play.daily.congratsNamed',
                'Congratulations {name}, you cleared every puzzle today! {tomorrow}')
                .replace('{name}', who)
                .replace('{tomorrow}', tomorrow)
            : t('play.daily.congrats', 'Congratulations — you cleared every puzzle today!')
                + ' ' + tomorrow;

        showPopup({
            title: t('play.daily.title', "That's all for today!"),
            text,
            mascot: 'blink',
            buttons: [
                { label: t('play.share.button', 'Share'), onClick: () => onShare({ name: '', email: '' }) },
                {
                    label: t('play.daily.home', 'Back to site'),
                    kind: 'secondary',
                    onClick: () => { location.href = '/'; },
                },
            ],
        });
        return;
    }

    // ── 首次：带留资表单 ──
    const form = document.createElement('div');
    form.className = 'sd-lead';

    const ask = document.createElement('p');
    ask.className = 'sd-lead__ask';
    ask.textContent = t('play.daily.giftAsk',
        'If you like, leave your name and email — we will send you a little gift when the game launches.');

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'sd-lead__input';
    nameInput.maxLength = 40;
    nameInput.autocomplete = 'nickname';
    nameInput.placeholder = t('play.daily.nickname', 'Nickname');

    const mailInput = document.createElement('input');
    mailInput.type = 'email';
    mailInput.className = 'sd-lead__input';
    mailInput.maxLength = 120;
    mailInput.autocomplete = 'email';
    mailInput.inputMode = 'email';
    mailInput.placeholder = t('play.daily.email', 'Email');

    // 回填分享前打过的内容
    nameInput.value = draft?.name ?? '';
    mailInput.value = draft?.email ?? '';

    const err = document.createElement('p');
    err.className = 'sd-lead__error';

    form.append(ask, nameInput, mailInput, err);

    const showError = key => {
        err.textContent = key === 'name'
            ? t('play.daily.needName', 'Please enter a nickname.')
            : t('play.daily.badEmail', "That email doesn't look right.");
        (key === 'name' ? nameInput : mailInput).focus();
    };

    const submit = async btn => {
        err.textContent = '';
        btn.disabled = true;
        const res = await onSubmit({ name: nameInput.value, email: mailInput.value });
        btn.disabled = false;

        if (!res.ok) { showError(res.error); return; }

        // 提交成功 → 原地换成道贺版，不必再点一次
        showDailyDone({ total, lead: res.lead, onSubmit, onShare });
    };

    mailInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            const btn = document.querySelector('.sd-popup__buttons .sd-btn--primary');
            if (btn) submit(btn);
        }
    });

    showPopup({
        title: t('play.daily.title', "That's all for today!"),
        text: t('play.daily.congrats', 'Congratulations — you cleared every puzzle today!'),
        mascot: 'blink',
        extra: form,
        buttons: [
            {
                label: t('play.daily.submit', 'Send it'),
                onClick: btn => submit(btn),
            },
            {
                label: t('play.share.button', 'Share'),
                kind: 'secondary',
                // 把还没提交的输入交出去，分享关掉后由调用方原样重建这个表单
                onClick: () => onShare({ name: nameInput.value, email: mailInput.value }),
            },
            {
                label: t('play.daily.skip', 'Maybe later'),
                kind: 'secondary',
                onClick: () => {
                    showDailyDone({ total, lead: { name: '', email: '' }, onSubmit, onShare });
                },
            },
        ],
    });

    setTimeout(() => nameInput.focus({ preventScroll: true }), 60);
}

/**
 * 出错提示（关卡加载失败、SBN 非法等）。
 * @param {string} message
 * @param {() => void} onClose
 */
export function showError(message, onClose) {
    showPopup({
        title: t('play.error.title', 'Something went wrong'),
        text: message,
        mascot: 'cry',
        buttons: [{ label: t('play.close', 'Close'), onClick: onClose }],
    });
}
